import { clsx } from 'clsx';
import * as React from "react";
import { lazy, memo, Suspense } from "react";
import Dump from "../components/dump";
import { decodeHTMLEntities, strip_tags } from "../helpers/utils.ts";
import { ItemType, type PageRenderedItemData } from "../rest.interface";
import { ContentRenderMode } from "./contentRenderMode";
import RenderContent from "./renderContent";
import {
	getBgColorClass,
	getBgImageClass,
	getMarginClasses,
	getPaddingClasses,
	uniqueArray,
	responsiveHelper
} from "./renderFunctions";

const Contact = lazy(() => import('../contact/contact'));
const VimeoIframe = lazy(() => import('../components/vimeoIframe'));
const GoogleMap = lazy(() => import('../layout/map'));
const RenderSlider = lazy(() => import('./renderSlider.tsx'));
const Service = lazy(() => import('../layout/service'));
const LatestNews = lazy(() => import('../layout/latestNews'));
const Accordion = lazy(() => import('../components/accordion'));

type Props = {
	item: PageRenderedItemData
	itemId?: string
	itemLayoutClass?: string[]
}

function RenderItem({item, itemId = null, itemLayoutClass = [], ...otherProps}: Props) {
	let itemClass = ['h-full'];

	if (item.type !== 'reuse') { // this stops column overflow, bg colors will be set anyway by the reused content
		//itemStyle = getBgStyle(item);
		itemClass = itemClass.concat(getBgImageClass(item), getBgColorClass(item));
	}

	if (item?.size) {
		itemLayoutClass = itemLayoutClass.concat(responsiveHelper(item.size));
	}

	if (item?.order) {
		itemLayoutClass = itemLayoutClass.concat(responsiveHelper(item.order, 'order-'));
	}

	if (item?.class) {
		itemClass.push(item.class);
	}

	if (item?.textAlign) {
		itemLayoutClass = itemLayoutClass.concat(responsiveHelper(item.textAlign));
	}

	if (item?.textClass) {
		itemLayoutClass.push(item.textClass);
	} else if (item?.textColor) {
		itemLayoutClass.push('text-[' + item.textColor + ']');
	}

	itemLayoutClass = itemLayoutClass.concat(getMarginClasses(item));
	itemClass = itemClass.concat(getPaddingClasses(item));

	function renderBlock(item: PageRenderedItemData = null) {
		if (item?.type) {
			switch (item.type) {
				case ItemType.CODE:
					if (item.rendered) {
						return <span dangerouslySetInnerHTML={{__html: decodeHTMLEntities(strip_tags(item.rendered))}}/>
					} else if (item.content) {
						return <span dangerouslySetInnerHTML={{__html: decodeHTMLEntities(strip_tags(item.content.toString()))}}/>
					}
					return null;

				case ItemType.TEXT:
					if (item.rendered) {
						return <span dangerouslySetInnerHTML={{__html: item.rendered}}/>
					} else if (item.content) {
						return <span dangerouslySetInnerHTML={{__html: item.content}}/>
					}
					return null;

				case ItemType.TEXT_ANCHOR:
					if (item.rendered) {
						return <a href={item.href} dangerouslySetInnerHTML={{__html: item.rendered}}/>
					} else if (item.content) {
						return <a href={item.href} dangerouslySetInnerHTML={{__html: item.content}}/>
					}
					return null;

				case ItemType.FAQ:
					if (item.rendered) {
						return <Accordion header={<p>{item.title}</p>}>
							<span dangerouslySetInnerHTML={{__html: item.rendered}}/>
						</Accordion>
					} else if (item.content) {
						return <Accordion header={<p>{item.title}</p>}>
							<span dangerouslySetInnerHTML={{__html: item.content}}/>
						</Accordion>
					}
					return null;

				case ItemType.REUSE:
					return <RenderContent content={item?.page?.content} mode={ContentRenderMode.REUSE_BLOCK}/>;

				case ItemType.CONTACT:
					return <Contact content={item.content}/>

				case ItemType.NEWS:
					return <LatestNews item={item}/>

				case ItemType.SERVICE:
					return item?.page && <Service item={item.page}/>

				case ItemType.MAP:
					return <Suspense fallback=""><GoogleMap item={item}/></Suspense>

				case ItemType.CONTAINER:
					if (Array.isArray(item.content)) {
						return item.content.map((containerItem, cIndex) => {
							return <div
								key={"containerItem_" + cIndex}
								className={clsx(responsiveHelper(containerItem.marginBottom, 'mb-', true), containerItem.class)}
							>
								{renderBlock(containerItem)}
							</div>
						});
					}
					return <Dump value={item.content}/>

				case ItemType.VIMEO:
					return <>{item.vimeoId &&
						<div className="relative pt-[56.25%]">
							<VimeoIframe
								videoId={item.vimeoId}

								autoplay={item.vimeoAutoplay}
								muted={item.vimeoMute}
								loop={item.vimeoLoop}

								showTitle={item.vimeoShowTitle}
								showByline={item.vimeoShowByline}
								showPortrait={item.vimeoShowPortrait}
								showControls={item.vimeoShowControls}

								color={item.vimeoColor}

								className={"vimeoContainer absolute top-0 left-0 w-full h-full"}
							/>
						</div>}
					</>

				case ItemType.SLIDER:
					return <RenderSlider item={item}/>
			}
		}

		return <>
			<Dump value={item.type} label={"Missing renderer"}/>
			<Dump value={item.content}/>
		</>;
	}

	function renderBlockWrapper(item = null) {
		let toReturn = renderBlock(item);

		if (item?.wrapper) {
			switch (item.wrapper) {
				case 'div':
					return <div>{toReturn}</div>;
				case 'headerBox':
					return <div className="headerBox">
						<div>{toReturn}</div>
					</div>;
				case 'blockquote':
					return <blockquote className="md:text-center">
						<div className="stylistic-quote-mark-before font-medium" aria-hidden="true">&ldquo;</div>
						{toReturn}
						<div className="stylistic-quote-mark-after font-medium" aria-hidden="true">&bdquo;</div>
					</blockquote>;
			}
		}

		return toReturn;
	}

	return (<>
		{/*<Dump label="itemLayoutClass" value={itemLayoutClass}/>*/}

		<div id={itemId} className={clsx(uniqueArray(itemLayoutClass))} {...otherProps}>
			<div className={clsx(uniqueArray(itemClass))}>
				{renderBlockWrapper(item)}
			</div>
		</div>
	</>);
}

export default memo(RenderItem)
